import { FC, useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useTranslation } from 'react-i18next';
import { FaDownload } from 'react-icons/fa';

import API from 'core/Api';

import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import FormButtons from 'components/form/FormButtons';
import Button from 'components/Button';

import { formatDate } from 'utils/helpers/formatters';

interface IProps {
  methods: any;
  data: any;
  onSubmit: (data: any) => void;
  reportParams?: any;
}

const ReportForm: FC<IProps> = ({ methods, data, onSubmit, reportParams }) => {
  const { t } = useTranslation();
  const { formState } = methods;
  const { isSubmitting } = formState;
  const [employees, setEmployees] = useState(data?.user || []);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    methods.setValue('date_from', new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    methods.setValue('date_to', new Date());
  }, [methods]);

  const groups = methods.watch('group');

  useEffect(() => {
    const groupIds = groups?.map((gr: any) => gr.value);

    if (groupIds?.length) {
      const filteredEmployees = data?.user?.filter((empl: any) => {
        if (!empl.groups?.length) return groupIds.includes('null');

        return empl.groups.some((groupId: number) => groupIds.includes(groupId));
      });

      setEmployees(filteredEmployees);
      methods.setValue('user', filteredEmployees);
    } else {
      setEmployees(data?.user);
      methods.setValue('user', null);
    }
  }, [groups, data, methods]);

  const handleDownload = () => {
    if (!reportParams?.date_from || !reportParams?.date_to) return;

    setIsDownloading(true);

    const values = methods.getValues();
    const payload = {
      date_from: values.date_from ? formatDate(values.date_from, 'YYY/mm/dd') : null,
      date_to: values.date_to ? formatDate(values.date_to, 'YYY/mm/dd') : null,
      user: values.user?.map((user: any) => user.value),
      group: values.group?.map((group: any) => group.value),
      status: values.status?.map((status: any) => status.value),
    };

    API.post('reports/work-trips/download', payload)
      .then(res => {
        const fileUrl = res.data?.url || res.data;
        if (fileUrl) {
          window.open(fileUrl, '_blank');
        }
      })
      .finally(() => setIsDownloading(false));
  };

  return (
    <Form
      methods={methods}
      btnLabel={t('reports.submitBtn')}
      hideAllBtns
      onSubmit={onSubmit}
    >
      <Row>
        <Col lg={4}>
          <FormGroup
            controlId="formGroupWorkTripsGroups"
            label={t('reports.groups')}
            type="select"
            as="select"
            name="group"
            isMulti
            isClearable
            isSearchable
            allowSelectAll
            selectOptions={data?.group}
          />
        </Col>
        <Col lg={4}>
          <FormGroup
            controlId="formGroupWorkTripsEmployees"
            label={t('reports.employees')}
            type="select"
            as="select"
            name="user"
            isMulti
            isClearable
            isSearchable
            allowSelectAll
            showTotal
            limitOptions
            selectOptions={employees}
          />
        </Col>
        <Col lg={4}>
          <FormGroup
            controlId="formGroupWorkTripsStatus"
            label={t('workTrips.status')}
            type="select"
            as="select"
            name="status"
            isMulti
            isClearable
            isSearchable
            allowSelectAll
            selectOptions={data?.status}
          />
        </Col>
      </Row>
      <Row>
        <Col
          lg={6}
          xl={{
            offset: 3,
            span: 6,
          }}
        >
          <Row>
            <Col>
              <FormGroup
                controlId="formGroupWorkTripsDateFrom"
                label={t('reports.fromDate')}
                type="datepicker"
                name="date_from"
                dateFormat="dd.MM.yyyy"
                showMonthDropdown
                showYearDropdown
                isRequired
              />
            </Col>
            <Col>
              <FormGroup
                controlId="formGroupWorkTripsDateTo"
                label={t('reports.toDate')}
                type="datepicker"
                name="date_to"
                dateFormat="dd.MM.yyyy"
                showMonthDropdown
                showYearDropdown
                isRequired
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <div className="d-flex flex-column flex-sm-row justify-content-center mb-3">
        <FormButtons
          btnLabel={t('reports.submitBtn')}
          isSubmitting={isSubmitting}
          hideCancelBtn
        />
        {reportParams && (
          <div className="pt-2 pl-sm-3 text-center text-sm-auto">
            <Button
              btnType="secondary"
              disabled={isDownloading}
              loading={isDownloading}
              onClick={handleDownload}
            >
              <FaDownload
                size={14}
                className="mr-2"
              />
              {t('reports.downloadWorkTripsDataReport')}
            </Button>
          </div>
        )}
      </div>
    </Form>
  );
};

export default ReportForm;
